import {
  ALL_TRADES,
  findTradeSubserviceWithGroup,
  getTradeTheme,
  isBusinessDefinedTrade,
  TRADE_THEME_ORDER,
} from '../constants/mockData/allTrades';
import { normalizePricingForDisplay } from './formatPrice';
import {
  isBusinessDefinedSubservice,
  resolveSubserviceTagline,
  resolveSubserviceTitle,
} from './subserviceDisplay';

/**
 * Join API sub-service fields with catalog copy (ALL_TRADES) for storefront cards.
 */
export function enrichSubserviceForStorefront(sub, serviceType, financingAvailable) {
  const trade = ALL_TRADES.find((t) => t.id === serviceType) || getTradeTheme(serviceType);
  const withGroup =
    trade && sub?.SubServiceType
      ? findTradeSubserviceWithGroup(trade, sub.SubServiceType)
      : null;
  const group = withGroup?.group;

  const title = resolveSubserviceTitle(serviceType, sub);
  const tagline = resolveSubserviceTagline(sub);

  const productDesc =
    (typeof sub.Description === 'string' && sub.Description.trim()) ||
    (typeof sub.ProductDescription === 'string' ? sub.ProductDescription.trim() : '') ||
    (withGroup?.subservice?.description || '');

  const normalizedPricing = normalizePricingForDisplay(sub.Pricing);

  return {
    ...sub,
    ...(normalizedPricing ? { Pricing: normalizedPricing } : {}),
    _parentServiceType: serviceType,
    ServiceType: serviceType,
    ProductName: title,
    ProductDescription: productDesc,
    Tagline: tagline || undefined,
    tradeLabel: trade?.name || getTradeTheme(serviceType)?.name || serviceType,
    catalogGroupName: group?.name ?? null,
    catalogGroupDescription: group?.description ?? null,
    catalogGroupColor: group?.color ?? null,
    tradeAccentColor: trade?.color ?? getTradeTheme(serviceType)?.color ?? null,
    IsCustom: isBusinessDefinedSubservice(sub) || undefined,
    FinancingAvailable: Boolean(financingAvailable) || undefined,
  };
}

function subserviceRowsForStorefrontService(svc) {
  if (!svc || typeof svc !== 'object') return [];
  return Array.isArray(svc.SubServices) ? svc.SubServices : [];
}

export function flattenAndEnrichBusinessServices(services) {
  if (!Array.isArray(services)) return [];
  return services.flatMap((svc) =>
    subserviceRowsForStorefrontService(svc).map((row) =>
      enrichSubserviceForStorefront(row, svc.ServiceType, svc.FinancingAvailable),
    ),
  );
}

/**
 * Bucket enriched storefront services into trade → catalog-group sections
 * (same hierarchy as the dashboard service catalog). Order follows TRADE_THEME_ORDER
 * and each trade’s catalog group order. Groups with no name (custom / ungrouped) are
 * still returned so cards render; callers should skip empty group labels.
 *
 * @param {ReturnType<typeof enrichSubserviceForStorefront>[]} enrichedServices
 * @returns {{
 *   tradeId: string,
 *   tradeLabel: string,
 *   tradeAccentColor: string | null,
 *   icon: import('@mui/material').SvgIconComponent | null,
 *   serviceCount: number,
 *   groups: { groupId: string | null, groupName: string | null, services: object[] }[],
 * }[]}
 */
export function groupStorefrontServicesByTradeAndGroup(enrichedServices) {
  if (!Array.isArray(enrichedServices) || enrichedServices.length === 0) return [];

  /** @type {Map<string, object[]>} */
  const byTrade = new Map();
  for (const svc of enrichedServices) {
    const tradeId = String(svc._parentServiceType || svc.ServiceType || 'other');
    if (!byTrade.has(tradeId)) byTrade.set(tradeId, []);
    byTrade.get(tradeId).push(svc);
  }

  const knownOrder = TRADE_THEME_ORDER.filter((id) => byTrade.has(id));
  const unknownOrder = [...byTrade.keys()].filter((id) => !TRADE_THEME_ORDER.includes(id));
  const orderedTradeIds = [...knownOrder, ...unknownOrder];

  return orderedTradeIds.map((tradeId) => {
    const services = byTrade.get(tradeId) || [];
    const catalogTrade = ALL_TRADES.find((t) => t.id === tradeId);
    const theme = getTradeTheme(tradeId);
    const tradeLabel =
      catalogTrade?.name || theme?.name || services[0]?.tradeLabel || tradeId;
    const tradeAccentColor =
      catalogTrade?.color || theme?.color || services[0]?.tradeAccentColor || null;
    const icon = catalogTrade?.icon || theme?.icon || null;

    if (isBusinessDefinedTrade(catalogTrade || theme) || !catalogTrade?.groups?.length) {
      return {
        tradeId,
        tradeLabel,
        tradeAccentColor,
        icon,
        serviceCount: services.length,
        groups: [{ groupId: null, groupName: null, services }],
      };
    }

    /** @type {Map<string, { groupId: string, groupName: string, services: object[] }>} */
    const groupBuckets = new Map();
    const ungrouped = [];

    for (const svc of services) {
      const withGroup =
        svc?.SubServiceType != null
          ? findTradeSubserviceWithGroup(catalogTrade, svc.SubServiceType)
          : null;
      const group = withGroup?.group;
      if (group?.id) {
        if (!groupBuckets.has(group.id)) {
          groupBuckets.set(group.id, {
            groupId: group.id,
            groupName: group.name || null,
            services: [],
          });
        }
        groupBuckets.get(group.id).services.push(svc);
      } else {
        ungrouped.push(svc);
      }
    }

    const groups = catalogTrade.groups
      .filter((g) => groupBuckets.has(g.id))
      .map((g) => groupBuckets.get(g.id));

    if (ungrouped.length > 0) {
      groups.push({ groupId: null, groupName: null, services: ungrouped });
    }

    return {
      tradeId,
      tradeLabel,
      tradeAccentColor,
      icon,
      serviceCount: services.length,
      groups,
    };
  });
}

/** DOM id for storefront trade jump targets. */
export function storefrontTradeSectionId(tradeId) {
  return `storefront-trade-${String(tradeId || 'other').replace(/[^a-zA-Z0-9_-]/g, '')}`;
}
